import { FC, memo, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled, { css } from 'styled-components';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { LayoutConst } from 'core/modules';
import { CreateLinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { TeamLongreadType } from "../../pages/teams/TeamEdit";
import { ModuleFormat } from "../../constants/ModuleFormat";
import { ProjectCardTitle } from "../projectCard/styles";
import { LMSCardFormatTypes, LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetLMSImage } from 'shared/hooks/api/useGetLMSImage';
import { LikesBox } from '../likesBox';

/** 
* Показать конкурсные баллы 
* @param showCompetitionPoint 
*/

const SelectRing = styled(FlexBox) <{ active?: boolean }>`
  background: rgba(171, 174, 175, 0.85);
  border: 2px solid #FFF;
  box-sizing: border-box;
  border-radius: 34px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  color: white;

  @media(max-width: ${LayoutConst.screen.md}px) {
    z-index: 5;
    width: 24px;
    height: 24px;
  }

  ${p => p.active ? css`
    background: ${Colors.geoWhite100};
    border: 2px solid ${Colors.geoWhite100};
    box-sizing: border-box;
    border-radius: 34px;
  ` : null}
`;

export const LmsTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
`;

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`



export interface TeamCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;
  team?: ComputedTeam;
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
}

const TeamCard: FC<TeamCardProps> = (props) => {
  const {
    isLoading,
    isSelectable,
    isDeletable,
    isRemovableFromBook,
    isEditable,
    isRejected,
    tooltip,
    width,
    height,
    onApprove,
    onReject,
    onSelect,
    onShouldReload,
    onTeamClick,
    onRemoveFromBook,
    team,
    contextName,
    linkPrefix,
    linkTo,
    entityType,
    ...handlers
  } = props;

  const { i18n } = useTranslation();
  const isOnlines = AppTester.isOnlines();
  const isMedia = AppTester.isMediiia();
  const isMobile = useIsMobile();
  const isHsePermCourses = !!useRouteMatch('/teams/hsepermcourses')
  const isTimeLine = !!useRouteMatch('/timeline');

  const likes = team?.quCount || 0;
  const isLiked = team?.isQu || false;

  const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const LMSTitleImage = useGetLMSImage(team);

  const isLMSModerationType = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const { modules: modulesRaw } = useGetModulesList(isLMSModerationType ? team?.teamId : '')

  const handleToolTipToggleMove = (val: boolean) => {
    setTooltipOpen && setTooltipOpen(val);
  }

  const LMSImageFiles = useMemo(() => {
    //@ts-ignore
    return (LMSTitleImage?.length > 0 && LMSTitleImage[0]?.files?.length > 0 && LMSTitleImage[0]?.files) || []
  }, [LMSTitleImage, team])

  const titleImages = getProjectImages(team, i18n.language);

  const handleTeamClick = (projectId?: string, e?: any) => {
    if (modulesRaw?.length === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(CreateLinkTo.projectNew(modulesRaw[0]), '_blank');
    } else {
      team && onTeamClick && onTeamClick(team, e);
    }

  }


  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS && !isHsePermCourses, [team?.moderationType, isHsePermCourses])

  const isUnitCourse = useMemo(() => {
    //@ts-ignore
    return team?.contexts?.includes(contextName)
  }, [contextName, team])

  const showLMSCardType = isLMS && team?.moderationType === ModerationType.LMS

  const lmsCardType = useMemo(() => {
    if (showLMSCardType) {
      return (team?.lmsCover !== LMSCardFormatTypes.short) ||
        (entityType && entityType !== "organizations")
        ? "t-shape"
        : undefined;
    }
    return undefined;
  }, [team, isUnitCourse, showLMSCardType, entityType]);

  const horizontalCover = useMemo(() => {
    //@ts-ignore
    return ((isLMS || isUnitCourse) && team?.lmsCourseTerm === LMSCourseDurationTypes.short)
  }, [isLMS, team, isUnitCourse])
  // @ts-ignore
  const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])

  const title = useMemo(() => {

    if (isTimeLine) return null;

    if (isLMS) {
      // @ts-ignore
      const newDate = new Date(team?.lmsModuleStartDate || null);
      const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      const day = newDate.getDate();
      const month = months[newDate.getMonth()];
      const format = team?.lmsModuleFormat;
      // @ts-ignore
      const price = (team?.lmsModulePrice || 0) + ' ₽';

      // @ts-ignore
      return <ProjectCardTitle><LmsTitle>{team?.name}<br />{(format === ModuleFormat.PreRecorded || format === ModuleFormat.Default) ? 'Онлайн' : 'Очная форма обучения'} | Старт {day} {month}{team?.lmsModulePrice ? ` | ${price}` : ''}</LmsTitle></ProjectCardTitle>

    }
    //@ts-ignore
    else if (team?.coverTitle?.length) {
      //@ts-ignore
      return <ProjectCardTitle>{team?.coverTitle}</ProjectCardTitle>
    }
    else if (team?.name?.length) {
      return (
      <ProjectCardTitle>
        {team?.name}
      </ProjectCardTitle>)
    } else return null;

  }, [team, isLMS, isUnitCourse, isTimeLine])

  return (
    <ProjectCardLayoutNew
      title={title}
      coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
      identifier={team?.teamId}
      onClick={handleTeamClick}
      titleImages={(isLMS && LMSImageFiles?.length > 0) ? LMSImageFiles : titleImages}
      isLoading={isLoading}
      width={width}
      team={team}
      height={height}
      isMoreVisible={true}
      isRejected={isRejected}
      metaAlwaysVisible={false}
      tooltipOpen={isTooltipOpen}
      toggleTooltip={handleToolTipToggleMove}
      linkPrefix={linkPrefix ? linkPrefix : undefined}
      needLmsCover={horizontalCover}
      linkTo={linkTo}
      projectType={lmsCardType}
      lmsCardType={lmsCardType}
      withoutGoToProject={!linkTo}
      author={
        isLMS ? teamOwner : !isOnlines && (
          <Author>
            <span style={{ color: isMedia && isMobile ? '#151514' : '#fefefe' }}>{team?.name}</span>
          </Author>
        )
      }
      likes={(() => {
        return team?.showQu ? <LikesBox likes={likes} isLiked={isLiked} isInteractive={false} /> : null;
      })()}
      isTeam
    />
  );
}
  ;

export default memo(TeamCard);
import { FC, memo, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled, { css } from 'styled-components';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { LayoutConst } from 'core/modules';
import { CreateLinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { TeamLongreadType } from "../../pages/teams/TeamEdit";
import { ModuleFormat } from "../../constants/ModuleFormat";
import { ProjectCardTitle } from "../projectCard/styles";
import { LMSCardFormatTypes, LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetLMSImage } from 'shared/hooks/api/useGetLMSImage';
import { LikesBox } from '../likesBox';

/** 
* Показать конкурсные баллы 
* @param showCompetitionPoint 
*/

const SelectRing = styled(FlexBox) <{ active?: boolean }>`
  background: rgba(171, 174, 175, 0.85);
  border: 2px solid #FFF;
  box-sizing: border-box;
  border-radius: 34px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  color: white;

  @media(max-width: ${LayoutConst.screen.md}px) {
    z-index: 5;
    width: 24px;
    height: 24px;
  }

  ${p => p.active ? css`
    background: ${Colors.geoWhite100};
    border: 2px solid ${Colors.geoWhite100};
    box-sizing: border-box;
    border-radius: 34px;
  ` : null}
`;

export const LmsTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
`;

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`



export interface TeamCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;
  team?: ComputedTeam;
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
}

const TeamCard: FC<TeamCardProps> = (props) => {
  const {
    isLoading,
    isSelectable,
    isDeletable,
    isRemovableFromBook,
    isEditable,
    isRejected,
    tooltip,
    width,
    height,
    onApprove,
    onReject,
    onSelect,
    onShouldReload,
    onTeamClick,
    onRemoveFromBook,
    team,
    contextName,
    linkPrefix,
    linkTo,
    entityType,
    ...handlers
  } = props;

  const { i18n } = useTranslation();
  const isOnlines = AppTester.isOnlines();
  const isMedia = AppTester.isMediiia();
  const isMobile = useIsMobile();
  const isHsePermCourses = !!useRouteMatch('/teams/hsepermcourses')
  const isTimeLine = !!useRouteMatch('/timeline');

  const likes = team?.quCount || 0;
  const isLiked = team?.isQu || false;

  const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const LMSTitleImage = useGetLMSImage(team);

  const isLMSModerationType = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const { modules: modulesRaw } = useGetModulesList(isLMSModerationType ? team?.teamId : '')

  const handleToolTipToggleMove = (val: boolean) => {
    setTooltipOpen && setTooltipOpen(val);
  }

  const LMSImageFiles = useMemo(() => {
    //@ts-ignore
    return (LMSTitleImage?.length > 0 && LMSTitleImage[0]?.files?.length > 0 && LMSTitleImage[0]?.files) || []
  }, [LMSTitleImage, team])

  const titleImages = getProjectImages(team, i18n.language);

  const handleTeamClick = (projectId?: string, e?: any) => {
    if (modulesRaw?.length === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(CreateLinkTo.projectNew(modulesRaw[0]), '_blank');
    } else {
      team && onTeamClick && onTeamClick(team, e);
    }

  }


  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS && !isHsePermCourses, [team?.moderationType, isHsePermCourses])

  const isUnitCourse = useMemo(() => {
    //@ts-ignore
    return team?.contexts?.includes(contextName)
  }, [contextName, team])

  const showLMSCardType = isLMS && team?.moderationType === ModerationType.LMS

  const lmsCardType = useMemo(() => {
    if (showLMSCardType) {
      return (team?.lmsCover !== LMSCardFormatTypes.short) ||
        (entityType && entityType !== "organizations")
        ? "t-shape"
        : undefined;
    }
    return undefined;
  }, [team, isUnitCourse, showLMSCardType, entityType]);

  const horizontalCover = useMemo(() => {
    //@ts-ignore
    return ((isLMS || isUnitCourse) && team?.lmsCourseTerm === LMSCourseDurationTypes.short)
  }, [isLMS, team, isUnitCourse])
  // @ts-ignore
  const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])

  const title = useMemo(() => {

    if (isTimeLine) return null;

    if (isLMS) {
      // @ts-ignore
      const newDate = new Date(team?.lmsModuleStartDate || null);
      const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      const day = newDate.getDate();
      const month = months[newDate.getMonth()];
      const format = team?.lmsModuleFormat;
      // @ts-ignore
      const price = (team?.lmsModulePrice || 0) + ' ₽';

      // @ts-ignore
      return <ProjectCardTitle><LmsTitle>{team?.name}<br />{(format === ModuleFormat.PreRecorded || format === ModuleFormat.Default) ? 'Онлайн' : 'Очная форма обучения'} | Старт {day} {month}{team?.lmsModulePrice ? ` | ${price}` : ''}</LmsTitle></ProjectCardTitle>

    }
    //@ts-ignore
    else if (team?.coverTitle?.length) {
      //@ts-ignore
      return <ProjectCardTitle>{team?.coverTitle}</ProjectCardTitle>
    }
    else if (team?.name?.length) {
      return (
      <ProjectCardTitle>
        {team?.name}
      </ProjectCardTitle>)
    } else return null;

  }, [team, isLMS, isUnitCourse, isTimeLine])

  return (
    <ProjectCardLayoutNew
      title={title}
      coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
      identifier={team?.teamId}
      onClick={handleTeamClick}
      titleImages={(isLMS && LMSImageFiles?.length > 0) ? LMSImageFiles : titleImages}
      isLoading={isLoading}
      width={width}
      team={team}
      height={height}
      isMoreVisible={true}
      isRejected={isRejected}
      metaAlwaysVisible={false}
      tooltipOpen={isTooltipOpen}
      toggleTooltip={handleToolTipToggleMove}
      linkPrefix={linkPrefix ? linkPrefix : undefined}
      needLmsCover={horizontalCover}
      linkTo={linkTo}
      projectType={lmsCardType}
      lmsCardType={lmsCardType}
      withoutGoToProject={!linkTo}
      author={
        isLMS ? teamOwner : !isOnlines && (
          <Author>
            <span style={{ color: isMedia && isMobile ? '#151514' : '#fefefe' }}>{team?.name}</span>
          </Author>
        )
      }
      likes={(() => {
        return team?.showQu ? <LikesBox likes={likes} isLiked={isLiked} isInteractive={false} /> : null;
      })()}
      isTeam
    />
  );
}
  ;

export default memo(TeamCard);
